import { useMemo, useState } from "react";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSalveazaTargetareAmbiguitate } from "@/hooks/api/mutate/useSalveazaTargetareAmbiguitate";
import { useGetTargetareScoruri } from "@/hooks/api/query/useGetTargetareScoruri";
import { useToast } from "@/hooks/use-toast";
import { formatNumar } from "@/utils/targetareFormatting";
import {
  definitieRaportAmbiguitate,
  randuriAmbiguitate,
} from "@/utils/targetareRaportScoruri";
import {
  FirmeCriteriuDialog,
  type FirmeCriteriuDeschis,
} from "../components/FirmeCriteriuDialog";
import { HeroPagina } from "../components/HeroPagina";
import { IstoricModificari } from "../components/IstoricModificari";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";
import { celulaAmbiguitate } from "../components/scoruri/CelulaAmbiguitate";
import { ScenariiScor } from "../components/scoruri/ScenariiScor";

export default function ScoruriTargetare() {
  const { toast } = useToast();
  const scoruri = useGetTargetareScoruri();
  const salveaza = useSalveazaTargetareAmbiguitate();
  const [firme, setFirme] = useState<FirmeCriteriuDeschis | null>(null);
  const [istoric, setIstoric] = useState(false);

  const date = scoruri.data;
  const randuri = useMemo(
    () => randuriAmbiguitate(date?.ambiguitati ?? []),
    [date]
  );
  const firmePeScor = useMemo(
    () =>
      new Map(
        (date?.firme_pe_scor ?? []).map((x) => [
          x.scor,
          { n: x.n, n_scop: x.n_scop },
        ])
      ),
    [date]
  );
  const peTip = date?.pe_tip ?? {};
  const scorMare = (date?.firme_pe_scor ?? [])
    .filter((x) => x.scor >= 90)
    .reduce((s, x) => s + x.n_scop, 0);
  const scorMic = (date?.firme_pe_scor ?? [])
    .filter((x) => x.scor < 50)
    .reduce((s, x) => s + x.n_scop, 0);
  const superAmbigue = (date?.ambiguitati ?? []).filter(
    (a) => a.super_ambiguu
  ).length;

  const celula = useMemo(
    () =>
      celulaAmbiguitate({
        inCurs: salveaza.isPending,
        onFirme: setFirme,
        onSalveaza: (patch) =>
          salveaza.mutate(patch, {
            onSuccess: () =>
              toast({
                title: "Serviciu actualizat",
                description:
                  "Ambiguitatea se aplică la următoarea reîncadrare a firmelor.",
              }),
            onError: (e) =>
              toast({
                title: "Salvarea a eșuat",
                description: e.message,
                variant: "destructive",
              }),
          }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salveaza.isPending]
  );

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Scoruri și ambiguitate"
        descriere={
          <>
            Scorul de potrivire al unui serviciu depinde de tipul de potrivire
            CAEN (neambiguu, ierarhic, ambiguu, super-ambiguu) și de prezența
            firmei pe o listă oficială sau neoficială. Aici se administrează
            cele {formatNumar(date?.scoruri.length ?? 0)} de scenarii de scor
            (aceleași ca în formularul de notificare), tipul de ambiguitate al
            fiecărui serviciu și marcajul super-ambiguu. Ambiguitatea pe fiecare
            cod CAEN se setează în pagina Mapare CAEN, iar regulile de încadrare
            au pagina lor. Modificările se văd în firme după reîncadrare.
          </>
        }
        isLoading={scoruri.isLoading}
        coloane={4}
        tiles={[
          { eticheta: "Scenarii de scor", valoare: date?.scoruri.length ?? 0 },
          {
            eticheta: "Servicii neambigue",
            valoare: peTip["Neambiguu"] ?? 0,
            ton: "bun",
            onClick: () =>
              setFirme({
                criteriu: { tip: "ambiguitate", valoare: "Neambiguu" },
              }),
          },
          {
            eticheta: "Servicii ambigue / ierarhice",
            valoare: (peTip["Ambiguu"] ?? 0) + (peTip["Ierarhic"] ?? 0),
            ton: "atentie",
            onClick: () =>
              setFirme({
                criteriu: { tip: "ambiguitate", valoare: "Ambiguu" },
              }),
          },
          {
            eticheta: "Servicii fără cod CAEN",
            valoare: peTip["N/A"] ?? 0,
            detaliu: "identificate doar prin liste",
          },
          {
            eticheta: "Servicii super-ambigue",
            valoare: superAmbigue,
            ton: "atentie",
          },
          {
            eticheta: "Firme în scop cu scor ≥ 90",
            valoare: scorMare,
            ton: "bun",
            onClick: () =>
              setFirme({
                criteriu: { tip: "scor", min: 90, max: 100 },
                titlu: "Firme în scop cu scor ≥ 90",
              }),
          },
          {
            eticheta: "Firme în scop cu scor < 50",
            valoare: scorMic,
            ton: "atentie",
            onClick: () =>
              setFirme({
                criteriu: { tip: "scor", min: 0, max: 49 },
                titlu: "Firme în scop cu scor sub 50",
              }),
          },
        ]}
        actiuni={
          <Button variant="outline" onClick={() => setIstoric((v) => !v)}>
            <History className="mr-2 h-4 w-4" />
            Istoric ({date?.istoric.length ?? 0})
          </Button>
        }
      />

      {scoruri.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Datele nu au putut fi încărcate: {scoruri.error.message}
        </p>
      )}

      {istoric && (
        <IstoricModificari
          modificari={date?.istoric ?? []}
          numeCheie={(cheie) =>
            date?.ambiguitati.find((a) => a.entity_id === cheie)?.name ?? cheie
          }
        />
      )}

      <section className="space-y-2">
        <h3 className="text-base font-heading font-semibold">
          Scenariile de scor
        </h3>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Scorul unei firme pentru un serviciu este cel al scenariului potrivit;
          firma primește scorul maxim dintre serviciile ei. Click pe număr
          deschide firmele cu acel scor.
        </p>
        {date && (
          <ScenariiScor
            scenarii={date.scoruri}
            firmePeScor={firmePeScor}
            onFirme={setFirme}
          />
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-heading font-semibold">
          Ambiguitatea pe servicii
        </h3>
        <RaportInteractiv
          definitie={definitieRaportAmbiguitate}
          randuri={randuri}
          isLoading={scoruri.isLoading}
          celula={celula}
        />
      </section>

      <FirmeCriteriuDialog deschis={firme} onClose={() => setFirme(null)} />
    </div>
  );
}
